import { Button, Flex, StrokeIcon, Tooltip, useToast } from '@acsbe/ui';
import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { useDomainSelection } from '../../../context/DomainSelectionContext';
import { DiscardChangesPopup } from '../../../ui/DiscardChangesPopup';
import { Logger } from '../../../utils/logger';
import { CustomInstallationConfig } from '../../../validations';
import { DEFAULT_WIDGET_CONFIG } from './hooks';
import {
  formatWidgetConfig,
  reverseFormatWidgetConfig,
} from './widgetSettings';

interface ContextualSaveBarProps {
  setWidgetConfig: (config: CustomInstallationConfig | null) => void;
}

const ToastContent = (props: { message: string }) => {
  return <>{props.message}</>;
};

export const ContextualSaveBar = (props: ContextualSaveBarProps) => {
  const toast = useToast();
  const {
    updateWidgetSettings,
    widgetSettings,
    changedConfig,
    setChangedConfig,
    showDiscardModal,
    setShowDiscardModal,
  } = useDomainSelection();
  const [saving, setSaving] = useState(false);

  // Block navigation within the app and show custom modal
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !!changedConfig && currentLocation.pathname !== nextLocation.pathname,
  );

  // Handle React Router navigation blocking
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowDiscardModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  // Still keep beforeunload for browser navigation (refresh, close tab, external links)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (changedConfig) {
        event.preventDefault();
        setShowDiscardModal(true);
        return '';
      }
    };

    if (changedConfig) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedConfig]);
  const onSave = async () => {
    try {
      setSaving(true);
      Logger.debug('changes', changedConfig);
      if (!changedConfig) return;
      const updatedWidgetSettings = reverseFormatWidgetConfig(changedConfig);
      await updateWidgetSettings(updatedWidgetSettings);
      setChangedConfig(null);
      //@ts-ignore
      toast(
        <ToastContent message="Widget customization changes published successfully." />,
        {
          scale: 'base',
          duration: 3000,
          icon: 'Check',
        },
      );
    } catch (e) {
      toast(
        <ToastContent message="Something went wrong while publishing your changes. Please try again later." />,
        {
          scale: 'danger',
          duration: 3000,
          icon: 'Warning',
        },
      );
      Logger.error('error updating widget settings');
    } finally {
      setSaving(false);
    }
  };

  const onDiscard = () => {
    setChangedConfig(null);
    props.setWidgetConfig(
      widgetSettings
        ? formatWidgetConfig(widgetSettings)
        : DEFAULT_WIDGET_CONFIG,
    );
  };

  const handleDiscardClick = () => {
    setShowDiscardModal(true);
  };

  const handleCloseDiscardModal = () => {
    setShowDiscardModal(false);
    // Reset the blocker when modal is closed
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleDiscardFromModal = () => {
    onDiscard();
    setShowDiscardModal(false);
    // Proceed with navigation after discarding
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const handlePublishFromModal = async () => {
    await onSave();
    setShowDiscardModal(false);
    // Proceed with navigation after saving
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  return (
    <>
      <Flex row gap={3}>
        <Tooltip
          content="Customize the widget and publish changes instantly to your store."
          ignoreDisabled
          scale="invert"
          zIndex={10}
        >
          <Button
            disabled={saving || !changedConfig}
            size="lg"
            scale="primary"
            onClick={onSave}
            data-mixpanel="save-customization"
            style={{ paddingInline: 12 }}
          >
            <StrokeIcon name="Pencil" scale={16} style={{ marginRight: 0 }} />
            Publish Changes
          </Button>
        </Tooltip>
      </Flex>

      {showDiscardModal && (
        <DiscardChangesPopup
          onClose={handleCloseDiscardModal}
          onDiscard={handleDiscardFromModal}
          onPublish={handlePublishFromModal}
          isPublishing={saving}
        />
      )}
    </>
  );
};
